import type { i18n as I18nInstance } from "i18next";
import {
  MarketingBlogArticles,
  type MarketingBlogArticleCard,
} from "@/components/redesign/MarketingBlogArticles";
import {
  MarketingBlogCategories,
  type MarketingBlogCategoryCard,
} from "@/components/redesign/MarketingBlogCategories";
import { CATEGORY_ICONS } from "@/components/redesign/blogCategoryIcons";
import type { BlogPost } from "@/utils/blog";
import {
  getAllCategoryIds,
  getCategoryPrefix,
  getCategorySlug,
} from "@/utils/blogCategories";
import { formatBlogPostDate } from "@/utils/formatBlogPostDate";

interface NotFoundBlogContentProps {
  posts: BlogPost[];
  lang: string;
  i18n: I18nInstance;
}

export default function NotFoundBlogContent({
  posts,
  lang,
  i18n,
}: Readonly<NotFoundBlogContentProps>) {
  const t = i18n.t.bind(i18n);

  const articles: MarketingBlogArticleCard[] = posts.slice(0, 3).map((post) => {
    const dateLabel = formatBlogPostDate(post.metadata.date, lang);
    return {
      id: post.id,
      title: post.metadata.title,
      description: post.metadata.description,
      href: `/${lang}/blog/${post.slug}`,
      imageSrc: post.metadata.ogImage,
      category: post.metadata.category
        ? t(`category.${post.metadata.category}`, { ns: "blog" })
        : undefined,
      meta: post.metadata.readingTime
        ? `${dateLabel} · ${t("blog.minRead", { count: post.metadata.readingTime })}`
        : dateLabel,
    };
  });

  const countByCategory = new Map<string, number>();
  for (const post of posts) {
    if (post.metadata.category) {
      countByCategory.set(
        post.metadata.category,
        (countByCategory.get(post.metadata.category) ?? 0) + 1,
      );
    }
  }

  const prefix = getCategoryPrefix(i18n);
  const categories: MarketingBlogCategoryCard[] = getAllCategoryIds()
    .filter((id) => (countByCategory.get(id) ?? 0) > 0)
    .map((id) => {
      const count = countByCategory.get(id) ?? 0;
      return {
        id,
        name: t(`category.${id}`, { ns: "blog" }),
        description: t(`categoryDescription.${id}`, { ns: "blog" }),
        countLabel: t("postsCount", { ns: "blog", count }),
        href: `/${lang}/blog/${prefix}/${getCategorySlug(id, i18n)}`,
        icon: CATEGORY_ICONS[id] ?? "bullhorn",
      };
    });

  return (
    <>
      {articles.length > 0 ? (
        <MarketingBlogArticles
          heading={t("notFound.latestArticles")}
          items={articles}
        />
      ) : null}

      {categories.length > 0 ? (
        <MarketingBlogCategories
          heading={t("notFound.browseCategories")}
          readMoreLabel={t("readArticles", { ns: "blog" })}
          items={categories}
        />
      ) : null}
    </>
  );
}
